import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import { useRouter } from 'next/router';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import { DrawerLayout, TaskLayout } from '@layouts';

import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { customerVehiclesServices, tradeTicketsServices } from '@services';

import { TaskSection } from 'components/common/TaskSection';
import { showError } from '@utils';
import { useToggleModal, useTradeTicketOperations } from '@hooks';
import {
  TRADE_TICKETS_STAGES_NAMES,
  TRADE_TICKETS_FIELD_NAMES,
} from 'components/tradeTickets/constants';
import { getPatchPayload } from 'hooks/tradeTickets/utils';
import { ENTITY_TYPES_COMMENTS } from '@constants';
import {
  ActivityListV2,
  CommentsV2,
  FACommentIcon,
  FADealership,
  FAEuroIcon,
  FAHistoryIcon,
  FAListCheckIcon,
  FAUserIcon,
  FAVehicleLeasing,
  FAVehicleSales,
  TabsV2,
  TaskDetailScrollableSection,
  TradeTicketDetails,
  TradeTicketForm,
  TradeTicketSendEmailsModal,
  TradeTicketUserDetails,
} from '@components';
import { UploadDocumentV3, UploadImageV3 } from 'components/common/media';
import { ButtonV2 } from 'components/common/ui';
import { MoreButton } from 'components/common/MoreButton';

export const tabs = [
  {
    label: 'Sale',
    id: 'vehicle-for-sale',
    icon: <FAVehicleSales />,
  },
  {
    label: 'Leasing',
    id: 'vehicle-for-leasing',
    icon: <FAVehicleLeasing />,
  },
  { label: 'Pricing', id: 'pricing', icon: <FAEuroIcon /> },
  {
    label: 'Dealership',
    id: 'dealership-appointment',
    icon: <FADealership />,
  },
  {
    label: 'Sourcing & Finance',
    id: 'sourcing-finance-review',
    icon: <FAListCheckIcon />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <FACommentIcon />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <FAHistoryIcon />,
  },
];

export const rightTabs = [
  {
    icon: <FAListCheckIcon />,
    id: 'ticket-details',
  },
  {
    icon: <FAUserIcon />,
    id: 'user-information',
  },
  { icon: <InsertDriveFileIcon />, id: 'documents' },
  { icon: <InsertPhotoIcon />, id: 'images' },
];

const TradeTicket = () => {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [note, setNote] = useState('');

  const tradeTicketId = router.query?.tradeTicketId;
  const {
    tradeTicket,
    assignees,
    stages,
    loading,
    reasonsOfRejection,
    comments,
    history,
    pricing,
    documents,
    media,
    getTradeTicket,
    updateTradeTicket,
    getComments,
    getDocuments,
    updateTradeTicketPricing,
    addTradeTicketPricing,
    getPricing,
    uploadFiles,
  } = useTradeTicketOperations();
  const submitBtnRef = useRef();
  const { isOpen, handleOpen, handleClose } = useToggleModal();

  const taskStatusNames = Object.keys(TRADE_TICKETS_STAGES_NAMES).reduce(
    (acc, cur) => {
      acc[cur] = TRADE_TICKETS_STAGES_NAMES[cur].label;

      return acc;
    },
    {}
  );

  useEffect(() => {
    if (tradeTicket?.ticketDetails?.note) {
      setNote(tradeTicket.ticketDetails.note);
    } else {
      setNote('');
    }
  }, [tradeTicket?.ticketDetails?.note]);

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const valuesWithNote = { ...values, note };
      const payload = getPatchPayload(valuesWithNote);
      await updateTradeTicket(payload);
      await getTradeTicket();
      await getComments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);

      await tradeTicketsServices.addTicketTradeComment({
        entityId: tradeTicketId,
        comment,
        entityType: ENTITY_TYPES_COMMENTS.instatrade,
      });
      await getComments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      await tradeTicketsServices.editTicketTradeComment(commentId, comment);
      await getComments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      await tradeTicketsServices.deleteTicketTradeComment(commentId);
      await getComments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFile = async (docId) => {
    try {
      setIsSubmitting(true);
      await tradeTicketsServices.deleteTradeTicketFile(tradeTicketId, docId);
      await getDocuments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadFiles = async (docs) => {
    try {
      setIsSubmitting(true);

      await uploadFiles(docs);

      await getDocuments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadMedia = async (docs) => {
    try {
      setIsSubmitting(true);
      await uploadFiles(docs);

      await getDocuments();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeTicketDetails = async ({ field, value }) => {
    if (field === 'note') {
      setNote(value);
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        [field]: value || null,
      };

      await updateTradeTicket(payload);
      await getTradeTicket();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStage = async (newStage) => {
    const payload = {
      stage: newStage,
    };
    try {
      setIsSubmitting(true);
      await updateTradeTicket(payload);

      await getTradeTicket();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePricing = async (pricingId = '') => {
    try {
      setIsSubmitting(true);
      await tradeTicketsServices.deleteTicketTradePricing(
        tradeTicketId,
        pricingId
      );
      await getPricing();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderHeaderOptions = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <MoreButton
          moreOptions={[
            {
              title: 'Send Dealer Email',
              onClick: handleOpen,
            },
          ]}
        />
        <ButtonV2
          ref={submitBtnRef}
          variant="contained"
          type="submit"
          form="trade-ticket-form"
          disabled={isSubmitting}
          size="small"
        >
          Save
        </ButtonV2>
      </Box>
    );
  };

  const handleValidateVin = async (vin) => {
    try {
      setIsSubmitting(true);
      const results = await customerVehiclesServices.validateByVin({ vin });
      return results;
    } catch (error) {
      showError('VIN validation failed');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>{`Instatrade - trade ticket: ${tradeTicketId} `}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={tradeTicketId}
        entity={ENTITIES_PERMISIONS.ReadInstaTrade}
        renderHeaderOptions={renderHeaderOptions}
        tabs={tabs}
        isLoading={loading}
        stages={stages}
        currentStage={tradeTicket?.ticketDetails?.stage}
        onChangeStage={handleChangeStage}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="trade-ticket-details" />

            <TaskDetailScrollableSection id="trade-ticket-details">
              <TradeTicketForm
                tradeTicketId={tradeTicketId}
                defaultValues={{ ...tradeTicket, pricing }}
                onSubmit={handleSubmit}
                onAddPricing={addTradeTicketPricing}
                onEditPricing={updateTradeTicketPricing}
                onDeletePricing={handleDeletePricing}
                onValidateVin={handleValidateVin}
                isLoading={isSubmitting || loading}
                onGetTradeTicket={getTradeTicket}
              />
              <TaskSection title="Comments" id={tabs[5].id} icon={tabs[5].icon}>
                <CommentsV2
                  comments={comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={assignees}
                />
              </TaskSection>
              <TaskSection title="History" id={tabs[6].id} icon={tabs[6].icon}>
                <ActivityListV2
                  activities={history}
                  taskFieldNames={TRADE_TICKETS_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2
              tabs={rightTabs}
              observerRootId="trade-vehicle-details-right"
            />

            <TaskDetailScrollableSection id="trade-vehicle-details-right">
              <TaskSection
                isSmall
                title="Ticket Details"
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TradeTicketDetails
                  task={tradeTicket}
                  assignees={assignees}
                  stages={stages}
                  reasonsOfRejection={reasonsOfRejection}
                  onChange={handleChangeTicketDetails}
                  note={note}
                />
              </TaskSection>

              <TaskSection
                isSmall
                title="User Information"
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TradeTicketUserDetails
                  user={tradeTicket?.userInformation || {}}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Documents"
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadDocumentV3
                  documents={documents}
                  onChange={handleUploadFiles}
                  onDeleteDocument={handleDeleteFile}
                  loading={loading || isSubmitting}
                  saveOnchange
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Media"
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
              >
                <UploadImageV3
                  images={media}
                  onChange={handleUploadMedia}
                  onDeleteImage={handleDeleteFile}
                  loading={loading || isSubmitting}
                  subtitle="Επίλεξε τα βίντεο που θες να ανεβάσεις"
                  accept={{
                    'video/mp4': ['.mp4', '.MP4'],
                    'video/quicktime': ['.mov', '.MOV'],
                  }}
                  maxSize={100}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
        {isOpen && (
          <TradeTicketSendEmailsModal
            tradeTicketId={tradeTicketId}
            isOpen={isOpen}
            onClose={handleClose}
          />
        )}
      </TaskLayout>
    </>
  );
};

TradeTicket.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default TradeTicket;
